import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

export interface ILanguageSelectorItem {
  code: string;
  /** The language's own name for itself. Never translated — see below. */
  label: string;
  /** The Angular locale id registered in `app.module.ts`. */
  locale: string;
}

/**
 * Where the choice is remembered.
 *
 * Plain `localStorage`, not `StorageService`: that one is async and initialises
 * inside `CoreProvider.init()`, which is *after* the first render. The language
 * has to be known before anything paints. `app.module.ts` reads the same key to
 * resolve `LOCALE_ID`, which is why the name lives here and not inline.
 */
export const LANG_CODE_KEY = 'langCode';

@Injectable({
  providedIn: 'root',
})
export class I18nService {
  /**
   * Labels are **endonyms** and are deliberately not run through `translate`.
   * Somebody who opened the app in a language they cannot read has to be able to
   * find their own; "German" is no help to them, "Deutsch" is.
   */
  private _languageSelectorItems: ILanguageSelectorItem[] = [
    { code: 'en', label: 'English', locale: 'en-US' },
    { code: 'de', label: 'Deutsch', locale: 'de' },
    { code: 'es', label: 'Español', locale: 'es' },
    { code: 'sv', label: 'Svenska', locale: 'sv' },
  ];

  private _currLang$ = new BehaviorSubject<ILanguageSelectorItem>(this._languageSelectorItems[0]);

  constructor(private readonly translate: TranslateService) {
    // Before `use()`. The default language is what a key missing from the active
    // locale falls back to, and `en` is the source language — so a miss renders
    // the English key verbatim rather than the key's name.
    this.translate.setDefaultLang('en');

    try {
      const stored = localStorage.getItem(LANG_CODE_KEY);
      const lang = this._languageSelectorItems.find((itm) => itm.code === stored);
      if (lang) this._currLang$.next(lang);
    } catch (e) {
      console.warn('Could not read the stored language', e);
    }
  }

  /**
   * Loads the chosen locale file.
   *
   * Awaited first by `CoreProvider.init()`, which is what `appIsReady$` gates on,
   * so the shell never renders a key before its translation arrived.
   *
   * **Never rejects.** A locale file that fails to load leaves ngx-translate
   * falling back to the key, and the key *is* the English string — the app stays
   * entirely usable in English. A missing translation is cosmetic; a bootstrap
   * that stops here is not.
   */
  async init(): Promise<void> {
    try {
      await firstValueFrom(this.translate.use(this._currLang$.value.code));
    } catch (e) {
      console.warn('Could not load translations', e);
    }
  }

  /**
   * The async counterpart of `TranslateService.instant`, for a string needed
   * outside a template. `skipLangs` lets a call site opt out of translating for
   * specific languages and keep the English default.
   */
  tg(def: string, skipLangs: string[] = []): Promise<string> {
    return skipLangs.includes(this._currLang$.value.code)
      ? Promise.resolve(def)
      : firstValueFrom(this.translate.get(def));
  }

  get currLang$(): BehaviorSubject<ILanguageSelectorItem> {
    return this._currLang$;
  }

  get languageSelectorItems(): ILanguageSelectorItem[] {
    return this._languageSelectorItems;
  }

  /** The Angular locale for `| date` / `| number` and for `Intl.*`. */
  get angularLocale(): string {
    return this._currLang$.value.locale;
  }

  /**
   * Switching **reloads the page**, on purpose.
   *
   * Angular resolves `LOCALE_ID` once, while the injector is being built, and it
   * is not reactive. ngx-translate switches at runtime. Without a reload every
   * `| date` and `| number` would keep formatting in the previous language while
   * the words around them changed — a half-translated screen, which reads as a
   * bug rather than as a limitation.
   *
   * It costs nothing here: the switcher lives on `/login` and on the profile
   * page, and neither holds in-page work to lose. Do not "fix" this by dropping
   * the reload without also replacing every locale-sensitive pipe.
   */
  set curLangCode(value: ILanguageSelectorItem) {
    if (value.code === this._currLang$.value.code) return;
    try {
      localStorage.setItem(LANG_CODE_KEY, value.code);
    } catch (e) {
      console.warn('Could not store the language', e);
    }
    this._currLang$.next(value);
    void this.translate.use(value.code);
    window.location.reload();
  }
}
